import { X } from "lucide-react";
import { LuFlame } from "react-icons/lu";
import { useGetPersistentMemory, useSupersedePersistentMemory } from "../../../api/queryHooks";
import UserAuthStore from "../../../store/userStore";
import { useSparkContext } from "../../../context/SparkContext";
import logger from "../../../services/Logger";

const TYPE_COLOURS = {
  constraint: "bg-fill-red-50 text-ink-red-800 dark:bg-fill-red-900/30 dark:text-ink-red-400 border-edge-red-200 dark:border-edge-red-700",
  preference: "bg-fill-hex-faf5ff text-ink-purple-600 dark:bg-fill-hex-581c87/30 dark:text-ink-hex-c084fc border-edge-hex-e9d5ff dark:border-edge-hex-7e22ce",
  fact: "bg-fill-blue-50 text-ink-blue-600 dark:bg-fill-blue-900/30 dark:text-ink-blue-400 border-edge-blue-200 dark:border-edge-blue-700",
  default: "bg-fill-gray-100 text-ink-gray-600 dark:bg-fill-gray-700 dark:text-ink-gray-300 border-edge-gray-200 dark:border-edge-gray-600",
};

const typeBadgeClass = (type) =>
  `inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${
    TYPE_COLOURS[type] || TYPE_COLOURS.default
  }`;

const MemoryPanel = () => {
  const { chat_id } = useSparkContext();
  const { orgId, user } = UserAuthStore.getState();

  const { data, isLoading } = useGetPersistentMemory(orgId, user?.uid, chat_id);
  const { mutate: supersede } = useSupersedePersistentMemory({
    onError: (err) => logger.error("MemoryPanel", "supersede failed", { error: err?.message }),
  });

  const entries = data?.entries ?? [];
  const active = entries
    .map((entry, index) => ({ ...entry, index }))
    .filter((e) => !e.superseded);

  const handleDismiss = (index) => {
    supersede({ orgId, userId: user?.uid, chatId: chat_id, index });
  };

  return (
    <div className="p-2">
      <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-edge-gray-200 dark:border-edge-panel-border mb-1.5">
        <LuFlame size={13} className="text-ink-gray-500 dark:text-ink-gray-400" />
        <span className="text-xs font-semibold text-ink-gray-500 dark:text-ink-gray-400">
          Spark Memory
        </span>
        {active.length > 0 && (
          <span className="ml-auto text-[10px] text-ink-gray-400 dark:text-ink-gray-500">
            {active.length} active
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="px-3 py-4 text-xs text-center text-ink-gray-400 dark:text-ink-gray-500">
          Loading…
        </div>
      ) : active.length === 0 ? (
        <div className="px-3 py-4 text-xs text-center text-ink-gray-400 dark:text-ink-gray-500">
          No memory entries yet.
          <br />
          Facts learned during this spark appear here.
        </div>
      ) : (
        <ul className="space-y-1 max-h-56 overflow-y-auto show-scrollbar px-1">
          {active.map((entry) => (
            <li
              key={entry.index}
              className="flex items-start gap-2 px-2 py-1.5 rounded-lg hover:bg-fill-gray-100 dark:hover:bg-fill-gray-700 group"
            >
              <span className={`flex-shrink-0 mt-0.5 ${typeBadgeClass(entry.type)}`}>
                {entry.type || "fact"}
              </span>
              <span className="flex-1 text-xs text-ink-gray-700 dark:text-ink-gray-200 leading-snug">
                {entry.text}
              </span>
              <button
                onClick={() => handleDismiss(entry.index)}
                className="flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-ink-gray-400 hover:text-ink-gray-700 dark:hover:text-ink-gray-200"
                aria-label="Dismiss memory entry"
              >
                <X size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MemoryPanel;
